/** Espelho local da posição de leitura, em IndexedDB.
 *
 * Serve a UMA coisa: retomada instantânea. Sem ele, reabrir o livro mostra a
 * página 1 até o `GET /reader/progress` responder — e na rede de uma escola
 * isso é um salto visível, no meio de uma aula, toda vez.
 *
 * ⚠️ Isto **não é leitura offline** (Fase 05). Guarda a POSIÇÃO, não o
 * conteúdo, e por isso não passa pela cache cifrada que a `CACHE_MASTER_KEY`
 * protege. Quando a Fase 05 chegar, este armazenamento é candidato natural a
 * ser absorvido por ela.
 *
 * IndexedDB cru, sem dependência nova: o que se faz aqui é um `get` e um `put`
 * por chave, e uma biblioteca para isso custaria mais bytes no dispositivo do
 * aluno do que as linhas que economiza.
 *
 * Tudo falha em silêncio. Em modo privado, com storage bloqueado por política
 * ou com a cota estourada, o `open` rejeita — e a consequência correta é o
 * leitor cair no servidor, não o livro não abrir.
 */

const BANCO = 'lumina-leitor'
const LOJA = 'progresso'
const VERSAO = 1

export interface PosicaoLocal {
  bookId: string
  page: number
  scrollPct: number
  locator: string | null
  /** ISO. É por ele que o last-write-wins (N19) decide contra o servidor. */
  clientUpdatedAt: string
}

function abrir(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB indisponível'))
      return
    }
    const pedido = indexedDB.open(BANCO, VERSAO)
    pedido.onupgradeneeded = () => {
      if (!pedido.result.objectStoreNames.contains(LOJA)) {
        pedido.result.createObjectStore(LOJA, { keyPath: 'bookId' })
      }
    }
    pedido.onsuccess = () => resolve(pedido.result)
    pedido.onerror = () => reject(pedido.error ?? new Error('Falha ao abrir o banco'))
    // Outra aba com uma versão mais nova segurou o upgrade: seguir sem cache é
    // melhor que travar a abertura do livro esperando a outra aba fechar.
    pedido.onblocked = () => reject(new Error('Banco bloqueado por outra aba'))
  })
}

function transacionar<T>(
  modo: IDBTransactionMode,
  operacao: (loja: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return abrir().then(
    (banco) =>
      new Promise<T>((resolve, reject) => {
        const transacao = banco.transaction(LOJA, modo)
        const pedido = operacao(transacao.objectStore(LOJA))
        pedido.onsuccess = () => resolve(pedido.result)
        pedido.onerror = () => reject(pedido.error ?? new Error('Falha na operação'))
        transacao.oncomplete = () => banco.close()
      }),
  )
}

/** Lê a posição guardada; `null` quando não há, ou quando o storage falhou. */
export async function lerPosicaoLocal(bookId: string): Promise<PosicaoLocal | null> {
  try {
    const registro = await transacionar<PosicaoLocal | undefined>('readonly', (loja) =>
      loja.get(bookId) as IDBRequest<PosicaoLocal | undefined>,
    )
    return registro ?? null
  } catch {
    return null
  }
}

/** Grava a posição. Falha em silêncio: perder o espelho não pode parar a leitura. */
export async function gravarPosicaoLocal(posicao: PosicaoLocal): Promise<void> {
  try {
    await transacionar('readwrite', (loja) => loja.put(posicao))
  } catch {
    /* modo privado, cota cheia, política de storage: o servidor segue valendo */
  }
}
